import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Trophy, TrendingDown, Target, Medal, Percent, Hash, BarChart3, TrendingUp } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, LineChart, Line, Legend, Cell
} from 'recharts';
import useScoreData from '../hooks/useScoreData';
import useFilteredGroups from '../hooks/useFilteredGroups';
import { sortUsersByPoints } from '../utils/scoreUtils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { cn } from '../lib/utils';

const CHART_COLORS = [
  '#e63946', '#457b9d', '#2a9d8f', '#e9c46a', '#f4a261',
  '#264653', '#a8dadc', '#6d6875', '#b5838d', '#ffb4a2',
  '#118ab2', '#06d6a0', '#ffd166', '#ef476f', '#073b4c'
];

// Entrances 200ms on a strong ease-out, exits 120ms. Only opacity and
// transform animate. The crossfade layer is opacity-only so the sections
// underneath own the 6px of travel instead of doubling it.
const ENTER_TRANSITION = { duration: 0.2, ease: [0.23, 1, 0.32, 1] };
const EXIT_TRANSITION = { duration: 0.12, ease: [0.23, 1, 0.32, 1] };
const SECTION_STAGGER = 0.05;
const MAX_STAGGERED_SECTIONS = 4;
const CHART_ANIMATION_DURATION = 300;

const CROSSFADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: EXIT_TRANSITION },
  transition: ENTER_TRANSITION
};

function shortName(name) {
  return name.replace(/'s\s+Picks\s*\d*/, '').replace(/\s+Picks\s*\d*/, '');
}

// One top-level block of the stats page. Sections arrive in sequence so the
// page reads as a single group landing rather than seven separate cards.
function Section({ index, reduce, children }) {
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, transform: 'translateY(6px)' }}
      animate={{ opacity: 1, transform: 'translateY(0px)' }}
      transition={{
        ...ENTER_TRANSITION,
        delay: reduce ? 0 : Math.min(index, MAX_STAGGERED_SECTIONS) * SECTION_STAGGER
      }}
    >
      {children}
    </motion.div>
  );
}

function computeStats(scoreData, filteredGroups) {
  if (!scoreData) return null;

  const { seasonScoreGroups, losers, pickStats } = scoreData;
  const groups = Object.keys(seasonScoreGroups);
  const allUsers = Object.keys(seasonScoreGroups[groups[0]] || {});

  const userStats = allUsers.map(name => {
    let totalPoints = 0;
    let totalMaxPoints = 0;
    let groupWins = 0;
    let timesLoser = 0;
    let bestGroup = null;
    let bestGroupPoints = -1;
    let worstGroup = null;
    let worstGroupPoints = Infinity;
    const groupScores = {};

    groups.forEach(group => {
      const userData = seasonScoreGroups[group][name];
      if (!userData || userData.maxPoints === 0) return;

      const pts = userData.points;
      const maxPts = userData.maxPoints;
      totalPoints += pts;
      totalMaxPoints += maxPts;
      groupScores[group] = { points: pts, maxPoints: maxPts };

      const sorted = Object.entries(seasonScoreGroups[group])
        .filter(([, u]) => u.maxPoints > 0)
        .sort(([, a], [, b]) => b.points - a.points);
      if (sorted.length > 0 && sorted[0][0] === name) {
        groupWins++;
      }

      if (losers[group] && losers[group].includes(name)) {
        timesLoser++;
      }

      const accuracy = maxPts > 0 ? pts / maxPts : 0;
      if (accuracy > bestGroupPoints) {
        bestGroupPoints = accuracy;
        bestGroup = group;
      }
      if (accuracy < worstGroupPoints) {
        worstGroupPoints = accuracy;
        worstGroup = group;
      }
    });

    const activeGroups = Object.keys(groupScores).length;

    return {
      name,
      totalPoints,
      totalMaxPoints,
      accuracy: pickStats[name]?.resolved > 0 ? totalPoints / pickStats[name].resolved : 0,
      groupWins,
      timesLoser,
      bestGroup,
      worstGroup,
      avgPointsPerGroup: activeGroups > 0 ? totalPoints / activeGroups : 0,
      groupScores,
      activeGroups
    };
  });

  userStats.sort((a, b) => b.totalPoints - a.totalPoints);

  const groupLeaders = {};
  filteredGroups.forEach(([group, scores]) => {
    const sorted = sortUsersByPoints(scores);
    const entries = Object.entries(sorted);
    if (entries.length > 0) {
      groupLeaders[group] = { name: entries[0][0], points: entries[0][1].points };
    }
  });

  return { userStats, groupLeaders };
}

function CustomTooltipBar({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-popover p-3 text-sm shadow-md">
      <p className="font-semibold text-foreground">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
}

function CustomTooltipLine({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-popover p-3 text-sm shadow-md max-h-48 overflow-y-auto">
      <p className="font-semibold text-foreground mb-1">Semanas {label}</p>
      {payload
        .filter(e => e.value != null)
        .sort((a, b) => b.value - a.value)
        .map((entry, i) => (
          <p key={i} className="flex justify-between gap-4" style={{ color: entry.color }}>
            <span>{shortName(entry.name)}</span>
            <span className="font-semibold">{entry.value}</span>
          </p>
        ))}
    </div>
  );
}

function StatsView({ year }) {
  const { scoreData, loading } = useScoreData(year);
  const filteredGroups = useFilteredGroups(scoreData, false);
  const shouldReduceMotion = useReducedMotion();

  const chartsAnimate = !shouldReduceMotion;
  const stats = loading ? null : computeStats(scoreData, filteredGroups);

  let body;

  if (loading) {
    body = (
      <motion.div key="loading" {...CROSSFADE}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-6 w-28" />
                <Skeleton className="h-4 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.div>
    );
  } else if (!stats) {
    body = (
      <motion.div key={`nodata-${year}`} {...CROSSFADE}>
        <div className="text-center py-12 text-muted-foreground">
          No hay datos disponibles para la temporada {year}
        </div>
      </motion.div>
    );
  } else {
    const { userStats, groupLeaders } = stats;
    const activeUsers = userStats.filter(u => u.totalMaxPoints > 0);
    const topUser = userStats[0];
    const bestAccuracy = [...userStats].sort((a, b) => b.accuracy - a.accuracy)[0];
    const mostWins = [...userStats].sort((a, b) => b.groupWins - a.groupWins)[0];
    const mostLosses = [...userStats].sort((a, b) => b.timesLoser - a.timesLoser)[0];

    const pointsBarData = [...activeUsers]
      .sort((a, b) => b.totalPoints - a.totalPoints)
      .map(u => ({ name: shortName(u.name), points: u.totalPoints, max: u.totalMaxPoints }));

    const trendData = filteredGroups.map(([group]) => {
      const row = { group };
      activeUsers.forEach(u => {
        const gs = u.groupScores[group];
        row[u.name] = gs ? gs.points : null;
      });
      return row;
    });

    const accuracyData = [...activeUsers]
      .sort((a, b) => b.accuracy - a.accuracy)
      .map(u => ({
        name: shortName(u.name),
        accuracy: parseFloat((u.accuracy * 100).toFixed(1))
      }));

    // The year is part of the key so switching seasons crossfades rather than
    // swapping numbers underneath the reader.
    body = (
      <motion.div key={`content-${year}`} className="space-y-8" {...CROSSFADE}>
        {/* Summary cards */}
        <Section index={0} reduce={shouldReduceMotion}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              icon={<Trophy className="w-5 h-5 text-accent" />}
              label="Líder general"
              value={shortName(topUser?.name || '-')}
              detail={`${topUser?.totalPoints || 0} pts`}
            />
            <StatCard
              icon={<Target className="w-5 h-5 text-primary" />}
              label="Mayor precisión"
              value={shortName(bestAccuracy?.name || '-')}
              detail={`${((bestAccuracy?.accuracy || 0) * 100).toFixed(1)}%`}
            />
            <StatCard
              icon={<Medal className="w-5 h-5 text-yellow-500" />}
              label="Más victorias"
              value={shortName(mostWins?.name || '-')}
              detail={`${mostWins?.groupWins || 0} semanas`}
            />
            <StatCard
              icon={<TrendingDown className="w-5 h-5 text-destructive" />}
              label="Más derrotas"
              value={shortName(mostLosses?.name || '-')}
              detail={`${mostLosses?.timesLoser || 0} semanas`}
            />
          </div>
        </Section>

        {/* Total points bar chart */}
        <Section index={1} reduce={shouldReduceMotion}>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Puntos totales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full" style={{ height: Math.max(300, pointsBarData.length * 40) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pointsBarData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                    <XAxis type="number" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }} />
                    <RechartsTooltip content={<CustomTooltipBar />} />
                    <Bar
                      dataKey="points"
                      name="Puntos"
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                      isAnimationActive={chartsAnimate}
                      animationDuration={CHART_ANIMATION_DURATION}
                      animationEasing="cubic-bezier(0.23,1,0.32,1)"
                    >
                      {pointsBarData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </Section>

        {/* Weekly trend line chart */}
        <Section index={2} reduce={shouldReduceMotion}>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Tendencia por semana
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="group" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <RechartsTooltip content={<CustomTooltipLine />} />
                    <Legend
                      formatter={(value) => shortName(value)}
                      wrapperStyle={{ fontSize: 12 }}
                    />
                    {activeUsers.map((u, i) => (
                      <Line
                        key={u.name}
                        type="monotone"
                        dataKey={u.name}
                        name={u.name}
                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls
                        isAnimationActive={chartsAnimate}
                        animationDuration={CHART_ANIMATION_DURATION}
                        animationEasing="cubic-bezier(0.23,1,0.32,1)"
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </Section>

        {/* Accuracy bar chart */}
        <Section index={3} reduce={shouldReduceMotion}>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Precisión (%)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={accuracyData} margin={{ left: 0, right: 20, top: 5, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                      angle={-30}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <RechartsTooltip content={<CustomTooltipBar />} />
                    <Bar
                      dataKey="accuracy"
                      name="Precisión"
                      radius={[4, 4, 0, 0]}
                      barSize={30}
                      isAnimationActive={chartsAnimate}
                      animationDuration={CHART_ANIMATION_DURATION}
                      animationEasing="cubic-bezier(0.23,1,0.32,1)"
                    >
                      {accuracyData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={entry.accuracy >= 60 ? '#2a9d8f' : entry.accuracy >= 50 ? '#e9c46a' : '#e63946'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </Section>

        {/* Leaderboard table */}
        <Section index={4} reduce={shouldReduceMotion}>
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Hash className="w-5 h-5 text-primary" />
                Clasificación general
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-left font-bold text-primary w-8">#</TableHead>
                      <TableHead className="text-left font-bold text-primary">Nombre</TableHead>
                      <TableHead className="text-center font-bold text-primary">Puntos</TableHead>
                      <TableHead className="text-center font-bold text-primary">Max</TableHead>
                      <TableHead className="text-center font-bold text-primary hidden sm:table-cell">
                        <Percent className="w-3 h-3 mx-auto" />
                      </TableHead>
                      <TableHead className="text-center font-bold text-primary hidden md:table-cell">Victorias</TableHead>
                      <TableHead className="text-center font-bold text-primary hidden md:table-cell">Derrotas</TableHead>
                      <TableHead className="text-center font-bold text-primary hidden lg:table-cell">Mejor</TableHead>
                      <TableHead className="text-center font-bold text-primary hidden lg:table-cell">Peor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeUsers.map((user, index) => (
                      <TableRow
                        key={user.name}
                        className={cn(
                          index === 0 && "bg-accent/5 border-l-2 border-l-accent"
                        )}
                      >
                        <TableCell className="font-bold text-muted-foreground">{index + 1}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {index === 0 && <Trophy className="w-4 h-4 text-accent" />}
                            <span className={cn("font-medium", index === 0 && "text-accent font-bold")}>
                              {user.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-semibold">{user.totalPoints}</TableCell>
                        <TableCell className="text-center text-muted-foreground">{user.totalMaxPoints}</TableCell>
                        <TableCell className="text-center hidden sm:table-cell">
                          <Badge variant={user.accuracy >= 0.6 ? "accent" : user.accuracy < 0.5 ? "destructive" : "secondary"} className="text-xs">
                            {(user.accuracy * 100).toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center hidden md:table-cell">
                          {user.groupWins > 0 ? (
                            <span className="text-sm font-semibold text-accent">{user.groupWins}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center hidden md:table-cell">
                          {user.timesLoser > 0 ? (
                            <span className="text-sm font-semibold text-destructive">{user.timesLoser}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center hidden lg:table-cell">
                          {user.bestGroup ? <Badge variant="outline" className="text-xs">Sem {user.bestGroup}</Badge> : '-'}
                        </TableCell>
                        <TableCell className="text-center hidden lg:table-cell">
                          {user.worstGroup ? <Badge variant="outline" className="text-xs">Sem {user.worstGroup}</Badge> : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </Section>

        {/* Group winners */}
        <Section index={5} reduce={shouldReduceMotion}>
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Medal className="w-5 h-5 text-yellow-500" />
                Ganadores por semana
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {filteredGroups.map(([group]) => {
                  const leader = groupLeaders[group];
                  return (
                    <div
                      key={group}
                      className="rounded-lg border p-4 hover:bg-muted/30 transition-colors duration-150"
                    >
                      <Badge variant="secondary" className="mb-2">Semanas {group}</Badge>
                      {leader ? (
                        <>
                          <div className="font-bold text-foreground truncate">{leader.name}</div>
                          <div className="text-sm text-accent font-semibold">{leader.points} pts</div>
                        </>
                      ) : (
                        <div className="text-sm text-muted-foreground">Sin datos</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </Section>

        {/* Per-group breakdown table */}
        <Section index={6} reduce={shouldReduceMotion}>
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Puntos por semana
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-left font-bold text-primary">Nombre</TableHead>
                      {filteredGroups.map(([group]) => (
                        <TableHead key={group} className="text-center font-bold text-primary whitespace-nowrap">
                          {group}
                        </TableHead>
                      ))}
                      <TableHead className="text-center font-bold text-primary">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeUsers.map((user, index) => (
                      <TableRow key={user.name}>
                        <TableCell>
                          <span className={cn("font-medium text-sm", index === 0 && "text-accent font-bold")}>
                            {user.name}
                          </span>
                        </TableCell>
                        {filteredGroups.map(([group]) => {
                          const gs = user.groupScores[group];
                          return (
                            <TableCell key={group} className="text-center text-sm">
                              {gs ? (
                                <span className="font-medium">{gs.points}</span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-center font-bold">{user.totalPoints}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </Section>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      {body}
    </AnimatePresence>
  );
}

function StatCard({ icon, label, value, detail }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
        </div>
        <div className="font-bold text-foreground truncate">{value}</div>
        <div className="text-sm text-muted-foreground">{detail}</div>
      </CardContent>
    </Card>
  );
}

export default StatsView;
